<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TalkviaLeadCapturesAPI from 'dashboard/api/talkviaLeadCaptures';

const loading = ref(true);
const saving = ref(false);
const captures = ref([]);
const websiteInboxes = ref([]);
const selectedId = ref(null);
const activeTab = ref('config');

const defaultFields = () => [
  { key: 'fullName', enabled: true, required: true },
  { key: 'phone', enabled: true, required: true },
  { key: 'cpf', enabled: true, required: true },
  { key: 'email', enabled: false, required: false },
  { key: 'birthDate', enabled: false, required: false },
];

const defaultCapture = () => ({
  id: null,
  name: 'Nova captação',
  status: 'draft',
  product: 'clt',
  experience: 'form',
  template_key: 'split',
  editor_mode: 'template',
  inbox_id: websiteInboxes.value[0]?.id || '',
  form_fields: defaultFields(),
  design: {
    primary_color: '#2563EB',
    secondary_color: '#0F172A',
    background_color: '#F1F5F9',
    card_color: '#FFFFFF',
    text_color: '#0F172A',
  },
  content: {
    eyebrow: 'Consulta rápida e segura',
    headline: 'Seu crédito aprovado em minutos',
    description: 'Preencha seus dados para receber atendimento personalizado.',
    form_title: 'Simule agora — é gratuito',
    button_label: 'QUERO MEU CRÉDITO',
    success_message:
      'Recebemos seus dados. Nossa equipe continuará o atendimento nesta conversa.',
  },
  seo: {
    title: '',
    description: '',
    keywords: '',
    canonical_url: '',
    noindex: false,
  },
  tracking: {
    ga4_id: '',
    google_ads_id: '',
    google_ads_label: '',
    meta_pixel_id: '',
    gtm_id: '',
  },
  custom_html:
    '<section class="hero">\n  <h1>Sua oportunidade começa aqui</h1>\n  <p>Preencha os dados para falar com nossa equipe.</p>\n</section>\n<div data-talkvia-form></div>',
  custom_js:
    "document.addEventListener('talkvia:submitted', event => {\n  console.log('Lead enviado', event.detail);\n});",
});

const editor = reactive(defaultCapture());

const fieldLabels = {
  fullName: 'Nome completo',
  phone: 'Telefone',
  cpf: 'CPF',
  email: 'E-mail',
  birthDate: 'Data de nascimento',
};

const tabs = [
  { key: 'config', label: 'Config', icon: 'i-lucide-sliders-horizontal' },
  { key: 'design', label: 'Design', icon: 'i-lucide-palette' },
  { key: 'content', label: 'Conteúdo', icon: 'i-lucide-layout-list' },
  {
    key: 'seo',
    label: 'SEO e pixels',
    icon: 'i-lucide-chart-no-axes-combined',
  },
  { key: 'code', label: 'HTML / JS', icon: 'i-lucide-code-2' },
];

const experienceOptions = [
  {
    value: 'form',
    label: 'Formulário direto',
    description: 'Coleta os dados de forma objetiva, sem etapa interativa.',
    icon: 'i-lucide-panels-top-left',
  },
  {
    value: 'roulette',
    label: 'Roleta',
    description: 'Cria uma experiência interativa antes do formulário.',
    icon: 'i-lucide-circle-dot-dashed',
  },
  {
    value: 'scratch',
    label: 'Raspadinha',
    description: 'Revela uma oferta antes de solicitar os dados do cliente.',
    icon: 'i-lucide-ticket-check',
  },
];

const palettes = [
  { name: 'Azul confiança', primary: '#2563EB', secondary: '#0F172A' },
  { name: 'Verde resultado', primary: '#16A34A', secondary: '#052E16' },
  { name: 'Roxo premium', primary: '#7C3AED', secondary: '#2E1065' },
  { name: 'Laranja energia', primary: '#EA580C', secondary: '#431407' },
  { name: 'Rosa conversão', primary: '#DB2777', secondary: '#500724' },
  { name: 'Grafite', primary: '#27272A', secondary: '#09090B' },
];

const enabledFields = computed(() =>
  editor.form_fields.filter(field => field.enabled)
);

const canSave = computed(
  () => editor.name.trim() && editor.inbox_id && enabledFields.value.length
);

const isPublished = computed(() => editor.status === 'published');

const clone = value => JSON.parse(JSON.stringify(value));

const applyCapture = capture => {
  Object.assign(editor, defaultCapture(), clone(capture));
  editor.inbox_id = Number(editor.inbox_id) || '';
  selectedId.value = capture.id;
};

const loadCaptures = async preferredId => {
  loading.value = true;
  try {
    const { data } = await TalkviaLeadCapturesAPI.get();
    captures.value = data.captures || [];
    websiteInboxes.value = data.website_inboxes || [];
    const selected =
      captures.value.find(item => item.id === preferredId) || captures.value[0];
    if (selected) applyCapture(selected);
    else Object.assign(editor, defaultCapture());
  } catch {
    useAlert('Não foi possível carregar as captações.');
  } finally {
    loading.value = false;
  }
};

const selectCapture = capture => applyCapture(capture);

const newCapture = () => {
  selectedId.value = null;
  Object.assign(editor, defaultCapture());
  activeTab.value = 'config';
};

const payload = () => ({
  name: editor.name.trim(),
  inbox_id: editor.inbox_id,
  status: editor.status,
  product: editor.product,
  experience: editor.experience,
  template_key: editor.template_key,
  editor_mode: editor.editor_mode,
  form_fields: clone(editor.form_fields),
  design: clone(editor.design),
  content: clone(editor.content),
  seo: clone(editor.seo),
  tracking: clone(editor.tracking),
  custom_html: editor.custom_html,
  custom_js: editor.custom_js,
});

const saveCapture = async () => {
  if (!canSave.value) {
    useAlert(
      'Informe um nome, escolha uma caixa de site e habilite ao menos um campo.'
    );
    return null;
  }

  saving.value = true;
  try {
    const response = editor.id
      ? await TalkviaLeadCapturesAPI.update(editor.id, payload())
      : await TalkviaLeadCapturesAPI.create(payload());
    await loadCaptures(response.data.id);
    useAlert('Captação salva.');
    return response.data;
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível salvar a captação.'
    );
    return null;
  } finally {
    saving.value = false;
  }
};

const publishCapture = async () => {
  const saved = await saveCapture();
  if (!saved) return;

  try {
    const { data } = await TalkviaLeadCapturesAPI.publish(saved.id);
    await loadCaptures(data.id);
    useAlert('Captação publicada.');
  } catch {
    useAlert('Não foi possível publicar a captação.');
  }
};

const duplicateCapture = async () => {
  if (!editor.id) return;
  try {
    const { data } = await TalkviaLeadCapturesAPI.duplicate(editor.id);
    await loadCaptures(data.id);
    useAlert('Cópia criada como rascunho.');
  } catch {
    useAlert('Não foi possível duplicar a captação.');
  }
};

const applyPalette = palette => {
  editor.design.primary_color = palette.primary;
  editor.design.secondary_color = palette.secondary;
};

const copyPublicLink = async () => {
  if (!editor.public_url) return;
  try {
    await navigator.clipboard.writeText(editor.public_url);
    useAlert('Link copiado.');
  } catch {
    useAlert('Não foi possível copiar o link.');
  }
};

const openPublicLink = () => {
  if (editor.public_url)
    window.open(editor.public_url, '_blank', 'noopener,noreferrer');
};

onMounted(() => loadCaptures());
</script>

<template>
  <section class="h-full w-full overflow-y-auto bg-n-surface-1 px-5 py-5">
    <div class="mx-auto flex w-full max-w-[1500px] flex-col gap-5">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="mb-1 text-2xl font-semibold text-n-slate-12">
            Captação WhatsApp
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            Crie páginas, receba o lead em uma caixa de site e acompanhe cada
            conversa no sistema.
          </p>
        </div>
        <Button
          label="Nova captação"
          icon="i-lucide-plus"
          @click="newCapture"
        />
      </header>

      <div
        class="grid min-h-[760px] gap-5 xl:grid-cols-[17rem_minmax(0,1fr)_24rem]"
      >
        <aside class="rounded-xl border border-n-weak bg-n-solid-2 p-3">
          <p class="px-2 pb-2 text-xs font-semibold uppercase text-n-slate-10">
            Suas captações
          </p>
          <div v-if="loading" class="p-3 text-sm text-n-slate-11">
            Carregando...
          </div>
          <div
            v-else-if="!captures.length"
            class="rounded-lg border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
          >
            Nenhuma captação criada. Comece pelo botão acima.
          </div>
          <div v-else class="grid gap-2">
            <button
              v-for="capture in captures"
              :key="capture.id"
              type="button"
              class="rounded-lg border p-3 text-left transition-colors"
              :class="
                capture.id === selectedId
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak hover:bg-n-alpha-2'
              "
              @click="selectCapture(capture)"
            >
              <span
                class="block truncate text-sm font-semibold text-n-slate-12"
                >{{ capture.name }}</span
              >
              <span
                class="mt-1 flex items-center justify-between text-xs text-n-slate-10"
              >
                <span>{{ capture.submissions_count }} leads</span>
                <span
                  :class="
                    capture.status === 'published'
                      ? 'text-n-teal-11'
                      : 'text-n-amber-11'
                  "
                >
                  {{
                    capture.status === 'published' ? 'Publicado' : 'Rascunho'
                  }}
                </span>
              </span>
            </button>
          </div>
        </aside>

        <main class="min-w-0">
          <div
            class="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
          >
            <input
              v-model="editor.name"
              class="min-w-56 flex-1 border-0 bg-transparent text-lg font-semibold text-n-slate-12 outline-none"
              placeholder="Nome da captação"
            />
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="editor.id"
                label="Duplicar"
                icon="i-lucide-copy-plus"
                color="slate"
                variant="faded"
                @click="duplicateCapture"
              />
              <Button
                :label="saving ? 'Salvando...' : 'Salvar'"
                icon="i-lucide-save"
                color="slate"
                variant="faded"
                :disabled="saving"
                @click="saveCapture"
              />
              <Button
                :label="isPublished ? 'Atualizar publicação' : 'Publicar'"
                icon="i-lucide-globe-2"
                :disabled="saving"
                @click="publishCapture"
              />
            </div>
          </div>

          <div
            class="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-n-weak bg-n-solid-2 p-1 md:grid-cols-5"
          >
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="flex h-10 items-center justify-center gap-2 rounded-lg text-sm font-medium"
              :class="
                activeTab === tab.key
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="activeTab = tab.key"
            >
              <span :class="tab.icon" class="size-4" />{{ tab.label }}
            </button>
          </div>

          <div class="grid gap-4">
            <template v-if="activeTab === 'config'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Destino e experiência
                </h2>
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="grid gap-2 text-sm text-n-slate-11"
                    >Caixa de site
                    <select
                      v-model="editor.inbox_id"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                    >
                      <option value="">Selecione...</option>
                      <option
                        v-for="inbox in websiteInboxes"
                        :key="inbox.id"
                        :value="inbox.id"
                      >
                        {{ inbox.name }}
                      </option>
                    </select>
                  </label>
                  <label class="grid gap-2 text-sm text-n-slate-11"
                    >Produto
                    <select
                      v-model="editor.product"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                    >
                      <option value="clt">CLT / Consignado</option>
                      <option value="fgts">FGTS</option>
                      <option value="inss">INSS</option>
                    </select>
                  </label>
                  <label
                    class="flex items-center justify-between rounded-lg border border-n-weak px-3 text-sm text-n-slate-12"
                  >
                    Modo avançado HTML / JS
                    <Switch
                      :model-value="editor.editor_mode === 'custom'"
                      @update:model-value="
                        editor.editor_mode = $event ? 'custom' : 'template'
                      "
                    />
                  </label>
                  <fieldset class="grid gap-2 md:col-span-2">
                    <legend class="text-sm text-n-slate-11">Experiência</legend>
                    <div class="grid gap-2 sm:grid-cols-3">
                      <button
                        v-for="experience in experienceOptions"
                        :key="experience.value"
                        type="button"
                        :aria-pressed="editor.experience === experience.value"
                        class="flex min-h-24 items-start gap-3 rounded-lg border p-3 text-left transition-colors"
                        :class="
                          editor.experience === experience.value
                            ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                            : 'border-n-weak text-n-slate-11 hover:border-n-strong hover:bg-n-alpha-2'
                        "
                        @click="editor.experience = experience.value"
                      >
                        <span
                          class="grid size-9 shrink-0 place-items-center rounded-lg"
                          :class="
                            editor.experience === experience.value
                              ? 'bg-n-brand/10 text-n-brand'
                              : 'bg-n-alpha-2 text-n-slate-11'
                          "
                        >
                          <span :class="experience.icon" class="size-4" />
                        </span>
                        <span class="min-w-0">
                          <strong class="block text-sm font-medium">
                            {{ experience.label }}
                          </strong>
                          <span
                            class="mt-1 block text-xs leading-5 text-n-slate-11"
                          >
                            {{ experience.description }}
                          </span>
                        </span>
                      </button>
                    </div>
                  </fieldset>
                </div>
              </section>

              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                  Formulário padrão do sistema
                </h2>
                <p class="mb-4 text-xs text-n-slate-11">
                  Os campos habilitados são validados e enviados para a conversa
                  da caixa de site.
                </p>
                <div class="grid gap-2 md:grid-cols-2">
                  <div
                    v-for="field in editor.form_fields"
                    :key="field.key"
                    class="flex items-center gap-3 rounded-lg border border-n-weak p-3"
                  >
                    <input
                      v-model="field.enabled"
                      type="checkbox"
                      class="size-4 accent-n-brand"
                    />
                    <span class="flex-1 text-sm text-n-slate-12">{{
                      fieldLabels[field.key]
                    }}</span>
                    <label
                      class="flex items-center gap-2 text-xs text-n-slate-10"
                      ><input
                        v-model="field.required"
                        type="checkbox"
                        :disabled="!field.enabled"
                      />
                      Obrigatório</label
                    >
                  </div>
                </div>
              </section>
            </template>

            <template v-else-if="activeTab === 'design'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Modelo do layout
                </h2>
                <div class="grid gap-2 md:grid-cols-3">
                  <button
                    v-for="item in [
                      { key: 'split', label: 'Conversão' },
                      { key: 'centered', label: 'Centralizado' },
                      { key: 'minimal', label: 'Minimalista' },
                    ]"
                    :key="item.key"
                    type="button"
                    class="h-20 rounded-lg border text-sm font-semibold"
                    :class="
                      editor.template_key === item.key
                        ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                        : 'border-n-weak text-n-slate-11'
                    "
                    @click="editor.template_key = item.key"
                  >
                    {{ item.label }}
                  </button>
                </div>
              </section>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                  Paletas prontas
                </h2>
                <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  <button
                    v-for="palette in palettes"
                    :key="palette.name"
                    type="button"
                    class="flex items-center gap-3 rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
                    @click="applyPalette(palette)"
                  >
                    <span class="flex"
                      ><i
                        class="size-5 rounded-full"
                        :style="{ background: palette.primary }" /><i
                        class="-ml-1 size-5 rounded-full"
                        :style="{ background: palette.secondary }" /></span
                    >{{ palette.name }}
                  </button>
                </div>
                <div class="mt-5 grid gap-3 sm:grid-cols-2">
                  <label
                    v-for="color in [
                      { key: 'primary_color', label: 'Cor principal' },
                      { key: 'secondary_color', label: 'Cor de apoio' },
                      { key: 'background_color', label: 'Fundo' },
                      { key: 'card_color', label: 'Cartão' },
                      { key: 'text_color', label: 'Texto' },
                    ]"
                    :key="color.key"
                    class="flex items-center justify-between rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
                  >
                    {{ color.label
                    }}<span class="flex items-center gap-2"
                      ><code>{{ editor.design[color.key] }}</code
                      ><input
                        v-model="editor.design[color.key]"
                        type="color"
                        class="size-8 cursor-pointer rounded border-0 bg-transparent"
                    /></span>
                  </label>
                </div>
              </section>
            </template>

            <section
              v-else-if="activeTab === 'content'"
              class="rounded-xl border border-n-weak bg-n-solid-2 p-5"
            >
              <h2 class="mb-4 text-sm font-semibold text-n-slate-12">
                Textos da página
              </h2>
              <div class="grid gap-4">
                <label
                  v-for="field in [
                    { key: 'eyebrow', label: 'Chamada superior' },
                    { key: 'headline', label: 'Título principal' },
                    { key: 'description', label: 'Descrição' },
                    { key: 'form_title', label: 'Título do formulário' },
                    { key: 'button_label', label: 'Texto do botão' },
                    { key: 'success_message', label: 'Mensagem de sucesso' },
                  ]"
                  :key="field.key"
                  class="grid gap-2 text-sm text-n-slate-11"
                  >{{ field.label }}
                  <textarea
                    v-model="editor.content[field.key]"
                    rows="2"
                    class="rounded-lg border border-n-weak bg-n-surface-1 p-3 text-n-slate-12"
                  />
                </label>
              </div>
            </section>

            <template v-else-if="activeTab === 'seo'">
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-4 text-sm font-semibold text-n-slate-12">SEO</h2>
                <div class="grid gap-4 md:grid-cols-2">
                  <label
                    v-for="field in [
                      { key: 'title', label: 'Título SEO' },
                      { key: 'description', label: 'Meta description' },
                      { key: 'keywords', label: 'Palavras-chave' },
                      { key: 'canonical_url', label: 'URL canônica' },
                    ]"
                    :key="field.key"
                    class="grid gap-2 text-sm text-n-slate-11"
                    >{{ field.label
                    }}<input
                      v-model="editor.seo[field.key]"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                  /></label>
                  <label class="flex items-center gap-3 text-sm text-n-slate-12"
                    ><input v-model="editor.seo.noindex" type="checkbox" />
                    Impedir indexação desta captação</label
                  >
                </div>
              </section>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <h2 class="mb-1 text-sm font-semibold text-n-slate-12">
                  Anúncios e pixels
                </h2>
                <p class="mb-4 text-xs text-n-slate-11">
                  Informe somente os IDs. O sistema carrega os scripts oficiais
                  com segurança.
                </p>
                <div class="grid gap-4 md:grid-cols-2">
                  <label
                    v-for="field in [
                      { key: 'ga4_id', label: 'Google Analytics 4 (G-...)' },
                      { key: 'gtm_id', label: 'Google Tag Manager (GTM-...)' },
                      { key: 'google_ads_id', label: 'Google Ads (AW-...)' },
                      { key: 'google_ads_label', label: 'Label de conversão' },
                      { key: 'meta_pixel_id', label: 'Meta Pixel ID' },
                    ]"
                    :key="field.key"
                    class="grid gap-2 text-sm text-n-slate-11"
                    >{{ field.label
                    }}<input
                      v-model="editor.tracking[field.key]"
                      class="h-10 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
                  /></label>
                </div>
              </section>
            </template>

            <template v-else>
              <section class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
                <div
                  class="mb-4 rounded-lg border border-n-amber-7 bg-n-amber-3 p-3 text-xs text-n-amber-11"
                >
                  O código roda somente na página pública, dentro de um iframe
                  isolado. Use
                  <code>&lt;div data-talkvia-form&gt;&lt;/div&gt;</code> para
                  posicionar o formulário padrão. Se não usar, ele será inserido
                  ao final.
                </div>
                <label class="grid gap-2 text-sm text-n-slate-11"
                  >HTML personalizado
                  <textarea
                    v-model="editor.custom_html"
                    rows="14"
                    spellcheck="false"
                    class="rounded-lg border border-n-weak bg-n-surface-1 p-3 font-mono text-xs text-n-slate-12"
                  />
                </label>
                <label class="mt-4 grid gap-2 text-sm text-n-slate-11"
                  >JavaScript personalizado
                  <textarea
                    v-model="editor.custom_js"
                    rows="10"
                    spellcheck="false"
                    class="rounded-lg border border-n-weak bg-n-surface-1 p-3 font-mono text-xs text-n-slate-12"
                  />
                </label>
              </section>
            </template>
          </div>
        </main>

        <aside class="min-w-0">
          <div class="sticky top-4 grid gap-4">
            <section class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="mb-1 text-sm font-semibold text-n-slate-12">
                    Publicação
                  </p>
                  <p class="mb-0 text-xs text-n-slate-11">
                    {{ editor.submissions_count || 0 }} leads recebidos
                  </p>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-xs"
                  :class="
                    isPublished
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                  >{{ isPublished ? 'Publicado' : 'Rascunho' }}</span
                >
              </div>
              <div v-if="editor.public_url" class="mt-3 flex gap-2">
                <input
                  :value="editor.public_url"
                  readonly
                  class="h-9 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-2 text-xs text-n-slate-11"
                /><Button
                  icon="i-lucide-copy"
                  size="sm"
                  color="slate"
                  variant="faded"
                  @click="copyPublicLink"
                /><Button
                  icon="i-lucide-external-link"
                  size="sm"
                  color="slate"
                  variant="faded"
                  @click="openPublicLink"
                />
              </div>
            </section>

            <section
              class="overflow-hidden rounded-xl border border-n-weak bg-white shadow-xl"
            >
              <div class="flex h-9 items-center gap-1.5 bg-slate-100 px-3">
                <i class="size-2.5 rounded-full bg-red-400" /><i
                  class="size-2.5 rounded-full bg-amber-400"
                /><i class="size-2.5 rounded-full bg-teal-500" />
              </div>
              <div
                v-if="editor.editor_mode === 'custom'"
                class="grid min-h-[560px] place-items-center p-6 text-center text-slate-600"
              >
                <div>
                  <span class="i-lucide-code-2 mx-auto mb-3 block size-8" />
                  <p class="font-semibold">HTML / JS personalizado</p>
                  <p class="text-xs">
                    A execução fica disponível somente no link público por
                    segurança.
                  </p>
                </div>
              </div>
              <div
                v-else
                class="min-h-[560px]"
                :style="{
                  background: editor.design.background_color,
                  color: editor.design.text_color,
                }"
              >
                <div
                  class="p-7 text-center text-white"
                  :style="{ background: editor.design.primary_color }"
                >
                  <p
                    class="mb-2 text-[10px] font-bold uppercase tracking-widest text-white/75"
                  >
                    {{ editor.content.eyebrow }}
                  </p>
                  <h2 class="mb-2 text-2xl font-bold leading-tight">
                    {{ editor.content.headline }}
                  </h2>
                  <p class="mb-0 text-xs text-white/80">
                    {{ editor.content.description }}
                  </p>
                </div>
                <div class="-mt-4 p-5">
                  <div
                    class="rounded-xl p-5 shadow-xl"
                    :style="{ background: editor.design.card_color }"
                  >
                    <p class="mb-4 text-center text-sm font-bold">
                      {{ editor.content.form_title }}
                    </p>
                    <div class="grid gap-2">
                      <div
                        v-for="field in enabledFields"
                        :key="field.key"
                        class="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs text-slate-500"
                      >
                        {{ fieldLabels[field.key] }}
                      </div>
                      <button
                        type="button"
                        class="mt-2 h-10 rounded-lg text-xs font-bold text-white"
                        :style="{ background: editor.design.primary_color }"
                      >
                        {{ editor.content.button_label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>
